import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataContext } from '../../context/DataContext';
import { useOrg } from '../../context/OrgContext';
import { MenuItem, OrderItem, ServiceType } from '../../types';
import Notification, { NotificationType } from '../../features/notifications/Notification';

const ClientCabinet: React.FC = () => {
  const { createOrder, menuItems, tables, menuCategories, loadingMenu, loadingCategories, loadingTables } = useDataContext();
  const { currentOrganization, loading: orgLoading } = useOrg();
  const navigate = useNavigate();
  
  const [currentOrder, setCurrentOrder] = useState<OrderItem[]>([]);
  const [serviceType, setServiceType] = useState<ServiceType>('dine-in');
  const [tableName, setTableName] = useState<string>('');
  const [customerName, setCustomerName] = useState<string>('Walk-in Customer');
  const [specialRequests, setSpecialRequests] = useState<string>('');
  const [notification, setNotification] = useState<{ type: NotificationType; message: string } | null>(null);

  // Show loading state if data is still loading
  if (orgLoading || loadingMenu || loadingCategories || loadingTables) {
    return (
      <div className="main-content">
        <div className="loading-container">
          <div className="loading-spinner-container">
            <div className="loading-spinner-text">Loading menu and tables...</div>
            <div className="loading-spinner-element"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show error if no organization is selected
  if (!currentOrganization) {
    return (
      <div className="main-content">
        <div className="hero" style={{ 
          background: 'linear-gradient(135deg, rgba(255, 59, 48, 0.05) 0%, rgba(255, 255, 255, 0.8) 100%)',
          paddingTop: '60px',
          paddingBottom: '60px'
        }}>
          <h1 style={{ fontSize: '2.5rem', color: '#ff3b30' }}>No Cafe Selected</h1>
          <p>Please select a cafe to manage orders.</p>
          <div style={{ marginTop: '1rem' }}>
            <button 
              onClick={() => navigate('/cafes')}
              className="btn btn-primary"
            >
              Select Cafe
            </button>
          </div>
        </div>
      </div>
    );
  }

  const menuByCategory = menuItems.reduce((acc, item) => {
    const categoryKey = item.categoryId?.toString() || 'uncategorized';
    if (!acc[categoryKey]) {
      acc[categoryKey] = [];
    }
    acc[categoryKey].push(item);
    return acc;
  }, {} as Record<string, MenuItem[]>);

  // Create a mapping of category IDs to category names
  const categoryNameMap = menuCategories.reduce((acc, category) => {
    acc[category.id] = category.name;
    return acc;
  }, {} as Record<number, string>);

  // Show message if no menu items are available
  if (menuItems.length === 0) {
    return (
      <div className="main-content">
        <div className="hero" style={{ 
          background: 'linear-gradient(135deg, rgba(255, 149, 0, 0.05) 0%, rgba(255, 255, 255, 0.8) 100%)',
          paddingTop: '60px',
          paddingBottom: '60px'
        }}>
          <h1 style={{ fontSize: '2.5rem', color: '#ff9500' }}>No Menu Items Available</h1>
          <p>There are no menu items configured for this cafe yet.</p>
          <div style={{ marginTop: '1rem' }}>
            <button 
              onClick={() => navigate(`/${currentOrganization.urlName}/menu`)}
              className="btn btn-primary"
            >
              Manage Menu
            </button>
            <button 
              onClick={() => navigate(`/${currentOrganization.urlName}/dashboard`)}
              className="btn btn-secondary"
              style={{ marginLeft: '1rem' }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const addToOrder = (menuItem: MenuItem) => {
    const existingItemIndex = currentOrder.findIndex(item => item.menuItem.id === menuItem.id);
    
    if (existingItemIndex >= 0) {
      const updatedOrder = [...currentOrder];
      updatedOrder[existingItemIndex].quantity += 1;
      setCurrentOrder(updatedOrder);
    } else {
      setCurrentOrder([...currentOrder, { menuItem, quantity: 1 }]);
    }
  };

  const removeFromOrder = (menuItemId: number) => {
    setCurrentOrder(currentOrder.filter(item => item.menuItem.id !== menuItemId));
  };

  const updateQuantity = (menuItemId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      setCurrentOrder(currentOrder.filter(item => item.menuItem.id !== menuItemId));
    } else {
      setCurrentOrder(currentOrder.map(item =>
        item.menuItem.id === menuItemId ? { ...item, quantity: newQuantity } : item
      ));
    }
  };

  const getItemQuantity = (menuItemId: number): number => {
    const item = currentOrder.find(item => item.menuItem.id === menuItemId);
    return item?.quantity || 0;
  };

  const getTotalAmount = (): number => {
    return currentOrder.reduce((total, item) => total + (item.menuItem.price * item.quantity), 0);
  };

  const handleSubmitOrder = async () => {
    if (currentOrder.length === 0) {
      setNotification({ type: 'warning', message: 'Please add items to your order first!' });
      return;
    }

    if (!customerName.trim()) {
      setNotification({ type: 'warning', message: 'Please enter your name!' });
      return;
    }

    if (serviceType === 'dine-in' && !tableName.trim()) {
      setNotification({ type: 'warning', message: 'Please select a table!' });
      return;
    }

    try {
      const orderData = {
        items: currentOrder,
        serviceType,
        tableName: serviceType === 'dine-in' ? tableName : undefined,
        customerName,
        specialRequests,
        status: 'pending' as const,
        total: getTotalAmount(),
        totalAmount: getTotalAmount(),
        source: 'client-cabinet' as const
      };

      await createOrder(orderData);
      setNotification({ type: 'success', message: `Thank you, ${customerName}! Your order has been sent to the kitchen.` });
      
      // Reset form
      setCurrentOrder([]);
      setCustomerName('Walk-in Customer');
      setSpecialRequests('');
      setTableName('');
    } catch (error) {
      console.error('Error creating order:', error);
      setNotification({ 
        type: 'error', 
        message: error instanceof Error ? error.message : 'Failed to place order. Please try again.' 
      });
    }
  };

  return (
    <div className="main-content">
      {/* Notification Display */}
      {notification && (
        <div style={{ 
          position: 'fixed', 
          top: '20px', 
          right: '20px', 
          zIndex: 1000,
          maxWidth: '400px'
        }}>
          <Notification
            type={notification.type}
            message={notification.message}
            onClose={() => setNotification(null)}
            autoClose={true}
            duration={4000}
          />
        </div>
      )}
      
      <div className="hero" style={{ 
        background: 'linear-gradient(135deg, rgba(48, 209, 88, 0.05) 0%, rgba(255, 255, 255, 0.8) 100%)',
        paddingTop: '60px',
        paddingBottom: '60px'
      }}>
        <h1 style={{ fontSize: '2.5rem' }}>Staff Order Management</h1>
        <p>Place orders on behalf of customers</p>
        <div style={{ marginTop: '1rem' }}>
          <button 
            onClick={() => navigate(`/${currentOrganization?.urlName || ''}/dashboard`)}
            className="btn btn-secondary"
            style={{ marginRight: '1rem' }}
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>

      <div className="table-order-grid">
        {/* Menu Section */}
        <div className="menu-section">
          {Object.entries(menuByCategory).map(([category, items]) => (
            <div key={category} className="menu-category">
              <h3>{categoryNameMap[parseInt(category, 10)] || 'Uncategorized'}</h3>
              {items.map(item => {
                const quantity = getItemQuantity(item.id);
                return (
                  <div key={item.id} className="menu-item">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                      {item.imageUrl && (
                        <div style={{ 
                          width: '80px', 
                          height: '80px', 
                          borderRadius: '12px', 
                          overflow: 'hidden', 
                          flexShrink: 0 
                        }}>
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="menu-item-info">
                        <div className="menu-item-name">{item.name}</div>
                        <div className="menu-item-description">{item.description}</div>
                        <div className="menu-item-price">${item.price.toFixed(2)}</div>
                      </div>
                    </div>
                    <div className="menu-item-actions">
                      <div className="quantity-controls">
                        <button 
                          className="quantity-btn"
                          onClick={() => {
                            if (quantity === 1) {
                              removeFromOrder(item.id);
                            } else {
                              updateQuantity(item.id, quantity - 1);
                            }
                          }}
                          disabled={quantity === 0}
                        >
                          −
                        </button>
                        <span style={{ 
                          minWidth: '32px', 
                          textAlign: 'center', 
                          fontWeight: '600', 
                          fontSize: '0.875rem', 
                          color: '#1d1d1f' 
                        }}>
                          {quantity}
                        </span>
                        <button 
                          className="quantity-btn"
                          onClick={() => {
                            if (quantity === 0) {
                              addToOrder(item);
                            } else {
                              updateQuantity(item.id, quantity + 1);
                            }
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Order Section */}
        <div className="order-section">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                Order Summary
                {currentOrder.length > 0 && (
                  <span style={{ 
                    backgroundColor: '#30d158', 
                    color: 'white', 
                    fontSize: '0.8rem', 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '12px', 
                    marginLeft: '0.5rem' 
                  }}>
                    {currentOrder.reduce((total, item) => total + item.quantity, 0)} items
                  </span>
                )}
              </h3>
            </div>
            
            {currentOrder.length > 0 ? (
              <div className="order-summary">
                                  {currentOrder.map(item => (
                    <div key={item.menuItem?.id || `item-${Math.random()}`} className="order-item">
                    <span>{item.menuItem?.name || 'Unknown Item'} × {item.quantity}</span>
                                          <span style={{ fontWeight: '600' }}>
                        ${((item.menuItem?.price || 0) * item.quantity).toFixed(2)}
                      </span>
                  </div>
                ))}
                <div className="order-total">
                  Total: ${getTotalAmount().toFixed(2)}
                </div>
              </div>
            ) : (
              <p style={{ color: '#666', textAlign: 'center', padding: '2rem' }}>
                No items added yet
              </p>
            )}

            {/* Order Details Form */}
            <div style={{ marginTop: '1.5rem' }}>
              <div className="form-group">
                <label className="form-label" htmlFor="customerName">Customer Name *</label>
                <input
                  id="customerName"
                  type="text"
                  className="form-input"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter customer name (default: Walk-in Customer)"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="serviceType">Service Type</label>
                <select
                  id="serviceType"
                  className="form-select"
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value as ServiceType)}
                >
                  <option value="dine-in">Dine In</option>
                  <option value="takeout">Takeout</option>
                </select>
              </div>

              {serviceType === 'dine-in' && (
                <div className="form-group">
                  <label className="form-label" htmlFor="tableSelect">Table</label>
                  <select
                    id="tableSelect"
                    className="form-select"
                    value={tableName}
                    onChange={(e) => setTableName(e.target.value)}
                  >
                    <option value="">Select a table</option>
                    {tables
                      .filter(table => table.isActive)
                      .map(table => (
                        <option key={table.id} value={table.name}>
                          {table.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label className="form-label" htmlFor="specialRequests">Special Requests</label>
                <textarea
                  id="specialRequests"
                  className="form-textarea"
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  placeholder="Any special requests or dietary restrictions..."
                  rows={3}
                />
              </div>

              <button
                className="btn btn-primary"
                onClick={handleSubmitOrder}
                style={{ width: '100%', marginTop: '16px' }}
                disabled={currentOrder.length === 0 || !customerName.trim()}
              >
                Place Order
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile floating action button */}
      <button
        className="mobile-place-order-btn"
        onClick={handleSubmitOrder}
        disabled={currentOrder.length === 0 || !customerName.trim()}
      >
        {currentOrder.length === 0 
          ? 'Add Items to Order' 
          : `Place Order - $${getTotalAmount().toFixed(2)}`
        }
      </button>
    </div>
  );
};

export default ClientCabinet; 